import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Chip,
  LinearProgress,
  Divider,
  Alert,
} from '@mui/material';
import {
  AccountCircle,
  Business,
  Email,
  Upgrade,
  Assessment,
  CheckCircle,
} from '@mui/icons-material';
import { useAuth } from 'oidc-react';
import { accountService } from '../services/accountService';
import { UserData } from '../types';
import { OidcUserProfile } from '../types/oidc';
import { useTokenState } from '../App';

const Account: React.FC = () => {
  const auth = useAuth();
  const [userData, setUserData] = useState<UserData>(accountService.getDefaultUserData());
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<UserData>(userData);
  const { isTokenValid } = useTokenState();

  // Versuche zuerst OIDC-Profil zu verwenden
  const oidcProfile = auth.userData?.profile as OidcUserProfile | undefined;

  useEffect(() => {
    const loadUserData = async () => {
      // Warte bis Token gültig ist
      if (!isTokenValid) {
        return;
      }

      if (oidcProfile) {
        // Erstelle UserData aus OIDC-Profil
        const oidcUserData = accountService.getUserDataFromOidc(oidcProfile);

        // Hole API Daten für usedBricks und resetDate
        try {
          const storedData = await accountService.getUserData();

          // Kombiniere OIDC-Daten mit API-Nutzungsdaten
          const combinedData: UserData = {
            ...oidcUserData,
            usedBricks: storedData.usedBricks || 0,
            resetDate: storedData.resetDate || oidcUserData.resetDate,
          };

          setUserData(combinedData);
          setFormData(combinedData);
        } catch (error) {
          console.error('Error loading user data from API:', error);
          // Fallback: Verwende nur OIDC-Daten
          setUserData(oidcUserData);
          setFormData(oidcUserData);
        }
      } else {
        // Fallback auf API
        try {
          const data = await accountService.getUserData();
          setUserData(data);
          setFormData(data);
        } catch (error) {
          console.error('Error loading user data:', error);
          // Verwende Default-Daten als letzten Fallback
          const defaultData = accountService.getDefaultUserData();
          setUserData(defaultData);
          setFormData(defaultData);
        }
      }
    };

    loadUserData();
  }, [oidcProfile, isTokenValid]);

  // Prüfe ob OIDC verwendet wird
  const isUsingOidc = !!(auth.userData?.profile as OidcUserProfile | undefined);

  const handleSave = async () => {
    // Speichere nur lokale Änderungen (nicht OIDC-Daten)
    if (!isUsingOidc) {
      try {
        const updatedData = await accountService.updateUserData(formData);
        setUserData(updatedData);
      } catch (error) {
        console.error('Error saving user data:', error);
        // Fallback: Setze formData trotzdem
        setUserData(formData);
      }
    } else {
      setUserData(formData);
    }
    setEditMode(false);
  };

  const handleCancel = () => {
    setFormData(userData);
    setEditMode(false);
  };

  const usagePercentage = accountService.getUsagePercentage();
  const remainingBricks = accountService.getRemainingBricks();

  const planFeatures = {
    free: ['200 Steine/Monat', 'Basis-Analysen', 'CSV Export'],
    // basic: ['10.000 Steine/Monat', 'Erweiterte Analysen', 'PDF & CSV Export', 'Email Support'],
    premium: ['10.000 Steine/Monat', 'Alle Features', 'Priority Support', 'API-Zugang'],
    // enterprise: ['Unbegrenzt', 'Dedicated Support', 'Custom Integration', 'SLA'],
  };

  // Bestimme den Upgrade-Plan basierend auf dem aktuellen Plan
  const getUpgradePlan = (): 'free' | 'premium' | null => {
    if (userData.plan === 'free') return 'premium';
    return null; // Kein Upgrade verfügbar
  };

  const upgradePlan = getUpgradePlan();
  const showUpgrade = upgradePlan !== null;

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: 'calc(100vh - 64px)', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Mein Account
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Verwalten Sie Ihre Kontoinformationen und Einstellungen
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* User Profile Card */}
          <Grid item xs={12} md={8}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Profil-Informationen
                  </Typography>
                  {!isUsingOidc && !editMode ? (
                    <Button variant="outlined" onClick={() => setEditMode(true)}>
                      Bearbeiten
                    </Button>
                  ) : !isUsingOidc && editMode ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button variant="outlined" onClick={handleCancel}>
                        Abbrechen
                      </Button>
                      <Button variant="contained" onClick={handleSave}>
                        Speichern
                      </Button>
                    </Box>
                  ) : null}
                </Box>

                {isUsingOidc && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Profil-Informationen werden von Ihrem Identity Provider verwaltet und können hier nicht bearbeitet werden.
                  </Alert>
                )}

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      disabled={isUsingOidc || !editMode}
                      InputProps={{
                        startAdornment: <AccountCircle sx={{ mr: 1, color: 'action.active' }} />,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="E-Mail"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      disabled={isUsingOidc || !editMode}
                      InputProps={{
                        startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Unternehmen"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      disabled={isUsingOidc || !editMode}
                      InputProps={{
                        startAdornment: <Business sx={{ mr: 1, color: 'action.active' }} />,
                      }}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Aktueller Plan
                  </Typography>
                  <Chip
                    label={accountService.getPlanName(userData.plan)}
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </CardContent>
            </Card>

            {/* Usage Card */}
            <Card elevation={2} sx={{ mt: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Assessment sx={{ color: 'primary.main', fontSize: 32 }} />
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      Kontingent-Nutzung
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Aktueller Monat
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {userData.usedBricks.toLocaleString('de-DE')} von {userData.monthlyLimit.toLocaleString('de-DE')} Steinen verwendet
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {usagePercentage.toFixed(1)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={usagePercentage}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: usagePercentage > 80 ? 'error.main' : usagePercentage > 50 ? 'warning.main' : 'success.main',
                      },
                    }}
                  />
                </Box>

                <Alert severity="info" icon={false}>
                  <Typography variant="body2">
                    <strong>{remainingBricks.toLocaleString('de-DE')} Steine</strong> verbleibend bis{' '}
                    {new Date(userData.resetDate).toLocaleDateString('de-DE')}
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          </Grid>

          {/* Plan Features Card */}
          <Grid item xs={12} md={4}>
            {showUpgrade ? (
              // Upgrade-Teaser für Premium Plan
              <Card elevation={2} sx={{ bgcolor: 'primary.50', borderLeft: 4, borderColor: 'primary.main' }}>
                <CardContent>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Upgrade sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                    <Chip label="Aktueller Plan" size="small" sx={{ mb: 1 }} />
                    <Typography variant="body1" fontWeight={600} gutterBottom>
                      {accountService.getPlanName(userData.plan)}
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="text.secondary">
                      {userData.monthlyLimit.toLocaleString('de-DE')} Steine/Monat
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
                      Upgrade auf {accountService.getPlanName(upgradePlan!)}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="primary.main">
                      {accountService.getPlanLimit(upgradePlan!).toLocaleString('de-DE')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Steine pro Monat
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                    {planFeatures[upgradePlan!].map((feature, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} />
                        <Typography variant="body2">{feature}</Typography>
                      </Box>
                    ))}
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    sx={{ mt: 2 }}
                    startIcon={<Upgrade />}
                  >
                    Auf {accountService.getPlanName(upgradePlan!)} upgraden
                  </Button>
                </CardContent>
              </Card>
            ) : (
              // Aktueller Plan ohne Upgrade
              <Card elevation={2} sx={{ bgcolor: 'success.50', borderLeft: 4, borderColor: 'success.main' }}>
                <CardContent>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                    <Chip label="Aktueller Plan" color="success" sx={{ mb: 1 }} />
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {accountService.getPlanName(userData.plan)}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="success.main">
                      {userData.monthlyLimit.toLocaleString('de-DE')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Steine pro Monat
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {planFeatures[userData.plan].map((feature, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} />
                        <Typography variant="body2">{feature}</Typography>
                      </Box>
                    ))}
                  </Box>

                  <Alert severity="success" sx={{ mt: 3 }}>
                    Sie nutzen bereits den Premium-Plan mit allen Features!
                  </Alert>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Account;
